import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { ProjectFile, ProjectFilePosition, RemoteFile } from '@types'

import { Modal } from '@components/Modal'

import { useModal } from '@hooks/app/modal.hook'
import { useFilePreviews } from '@hooks/file/file-preview.hook'
import { useProjectGuards } from '@hooks/project/project-guards.hook'
import { useObservation } from '@hooks/observation/observation.hook'
import { useUpdateObservation } from '@hooks/observation/update-observation.hook'

import { LocalizationEdit } from '@modules/_shared/components/LocalizationEdit'

export interface FormData {
  projectFile?: ProjectFile
  projectFilePosition?: ProjectFilePosition
}

export const EditObservationLocalizationPage = (): JSX.Element => {
  const { hideModal } = useModal()
  const { t } = useTranslation()
  const { projectId } = useParams()
  const [searchParams] = useSearchParams()

  const observationId = searchParams.get('observationId')

  const [plan, setPlan] = useState<RemoteFile>()

  const [formData, setFormData] = useState<FormData>({
    projectFile: undefined,
    projectFilePosition: undefined,
  })

  const { data } = useObservation(observationId || '')
  const { updateObservation } = useUpdateObservation()

  const { getProjectGuards } = useProjectGuards({ projectId })
  const { canEditObservation } = getProjectGuards()

  const { loadFilePreviews: loadPlanPreview } = useFilePreviews({
    files: formData?.projectFile?.file ? ([formData?.projectFile?.file] as RemoteFile[]) : undefined,
    onFilesLoaded: ([file]) => setPlan(file as RemoteFile),
  })

  const observation = data?.observation

  useEffect(() => {
    if (!observation) return

    setFormData({
      projectFile: observation.projectFile,
      projectFilePosition: observation.projectFilePositions ? observation.projectFilePositions[0] : undefined,
    })
  }, [observation])

  useEffect(() => {
    if (formData.projectFile) {
      loadPlanPreview()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.projectFile, observation?.projectFile?.file])

  const handleSubmit = () => {
    if (!observation) return

    updateObservation(
      {
        observation: {
          name: observation.name,
          tradeKey: observation.trade!.key,
          companyId: observation.company?.id,
          phaseKey: observation.phase?.key,
          limitDate: observation.limitDate,
          projectFileId: formData.projectFilePosition ? formData.projectFile?.id : undefined,
          projectFilePositions: formData.projectFilePosition
            ? [
                {
                  x: formData.projectFilePosition.x,
                  y: formData.projectFilePosition.y,
                },
              ]
            : undefined,
        },
        observationId: observation.id,
      },
      observation.files as RemoteFile[],
    )

    hideModal('update_observation_localization')
  }

  return (
    <Modal
      title={t('modules_project-observations_pages_edit-observation-localization_title')}
      onSubmitLabel={t('modules_project-observations_pages_edit-observation-localization_submit_title')}
      onSubmit={canEditObservation ? handleSubmit : undefined}
      onClose={() => hideModal('update_observation_localization')}
    >
      <LocalizationEdit
        file={plan}
        canEdit={canEditObservation}
        markerType="observation"
        projectFile={formData.projectFile}
        projectId={projectId || ''}
        data={{
          idNumber: observation?.idNumber,
          name: observation?.name,
          trade: observation?.trade,
          status: observation?.status,
          projectFilePositions: formData.projectFilePosition
            ? [
                {
                  x: formData.projectFilePosition.x,
                  y: formData.projectFilePosition.y,
                },
              ]
            : undefined,
        }}
        onChangeProjectFile={(_, projectFile) => {
          setFormData((prev) => ({
            ...prev,
            projectFile,
            projectFilePosition: projectFile ? prev.projectFilePosition : undefined,
          }))
        }}
        onAdd={(projectFilePosition: ProjectFilePosition) => setFormData((prev) => ({ ...prev, projectFilePosition }))}
        onChange={(projectFilePosition: ProjectFilePosition) =>
          setFormData((prev) => ({ ...prev, projectFilePosition }))
        }
        onRemove={() => setFormData((prev) => ({ ...prev, projectFilePosition: undefined }))}
      />
    </Modal>
  )
}
